// authentication.service.ts

import { Injectable, EventEmitter } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, firstValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Models } from 'appwrite';

import { User } from '../../models';
import { AppwriteService } from './appwrite.sdk.service';
import { ApiService } from './lsg.api.service';
import { PortalControlService } from './portal-control.service';
import { CrashReportsService } from './crash-reports.service';
import {
  SessionActions,
  LoginActions,
  TokenActions,
  UserProfileActions,
  OnboardingActions,
  RegistrationActions,
} from '../store/_features/user/auth/auth.actions';
import {
  selectToken,
  selectLearmoProfile,
  selectAppwriteProfile,
  selectIsAuthenticated,
  selectIsLoading,
} from '../store/_features/user/auth/auth.selectors';
import { environment } from 'src/environments/environment';

/**
 * Authentication Service - Maintains backward compatibility
 * 
 * Acts as a facade between components and NgRx store for authentication operations.
 * Preserves existing interface while delegating to store internally.
 */
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  // Event emitters (backward compatibility)
  onLogout: EventEmitter<boolean> = new EventEmitter<boolean>();
  onLogin: EventEmitter<boolean> = new EventEmitter<boolean>();

  public redirectUrl: string;

  // Observables from store (backward compatibility)
  public currentUser: Observable<User>;
  public appwriteProfile: Observable<Models.User<Models.Preferences>>;
  public token: Observable<string>;
  public isEmailConfirmed: Observable<boolean>;

  // Current values (backward compatibility)
  currentUserValue: User;
  appwriteProfileValue: Models.User<Models.Preferences>;
  currentTokenValue: string;

  private currentUserStorageKey = environment.storageKeys.currentUserStorageKey;
  private appwriteProfileStorageKey = environment.storageKeys.appwriteProfileStorageKey;
  private tokenStorageKey = environment.storageKeys.tokenStorageKey;

  private activeRefreshPromise: Promise<string> | null = null;

  constructor(
    private appwriteService: AppwriteService,
    private api: ApiService,
    private router: Router,
    private portalControlService: PortalControlService,
    private toastr: ToastrService,
    private store: Store,
    private crashReportsService: CrashReportsService
  ) {
    // Map store selectors to existing observable names
    this.appwriteProfile = this.store.select(selectAppwriteProfile);
    this.currentUser = this.store.select(selectLearmoProfile);
    this.token = this.store.select(selectToken);

    // Subscribe and update local values (backward compatibility)
    this.currentUser.subscribe((profile) => {
      this.currentUserValue = profile;
      if (profile) {
        localStorage.setItem(
          this.currentUserStorageKey,
          JSON.stringify(profile)
        );
        this.setIdentity(profile);
      }
    });

    this.appwriteProfile.subscribe((profile) => {
      this.appwriteProfileValue = profile;
      if (profile) {
        localStorage.setItem(
          this.appwriteProfileStorageKey,
          JSON.stringify(profile)
        );
      }
    });

    this.token.subscribe((token) => {
      this.currentTokenValue = token;
      if (token) {
        localStorage.setItem(this.tokenStorageKey, token);
        this.appwriteService.setCurrentToken(token);
      }
    });

    // Initialize state on service creation
    this.store.dispatch(SessionActions.initialize());
  }

  // Backward compatibility setter
  public set setCurrentAppwriteValue(profile: any) {
    localStorage.setItem(
      this.appwriteProfileStorageKey,
      JSON.stringify(profile)
    );
  }

  // ===== Token Validation Methods (backward compatibility) =====

  isTokenValidParse(token = this.currentTokenValue) {
    return this.appwriteService.isTokenValidParse(token);
  }

  async isTokenValid() {
    let isTokenValid = await this.appwriteService.isTokenValid(
      this.currentTokenValue
    );
    return isTokenValid;
  }

  // ===== Session Methods (backward compatibility) =====

  forget() {
    this.store.dispatch(SessionActions.forget({ redirect: true }));
  }

  async getUserSessions() {
    return await this.appwriteService.getUserSessions();
  }

  async getCurrentUserSession() {
    return await this.appwriteService.getUserCurrentSession();
  }

  async clearSession() {
    return await this.appwriteService.clearAppwriteSession();
  }

  async killSessionById(id) {
    return await this.appwriteService.killSessionByID(id);
  }

  // ===== Identity Methods =====

  setIdentity(currentUserValue: User = this.currentUserValue) {
    this.crashReportsService.setIdentity(currentUserValue);
  }

  removeIdentity() {
    this.crashReportsService.removeIdentity();
  }

  // ===== Login Response Handlers (backward compatibility) =====

  public onLoginResponse(response, forceAffiliate: boolean = false) {
    this.onLogin.emit();
  }

  handleUserLoginRedirect(forceAffiliate: boolean = false) {
    if (!!forceAffiliate) {
      this.router.navigate(['/affiliate']);
    } else {
      switch (this.currentUserValue?.role?.name) {
        case 'subscriber':
          if (!!this.currentUserValue.subscription.isSubscribed) {
            if (this.redirectUrl) {
              this.router.navigate([this.redirectUrl]);
              this.redirectUrl = null;
            } else {
              this.router.navigate(['dashboard']);
            }
          } else {
            this.router.navigate(['/choose-plan']);
          }
          break;
        case 'affiliate':
          this.router.navigate(['/affiliate']);
          break;
        default:
          this.router.navigate(['dashboard']);
          break;
      }
    }
  }

  public onVerifiedResponse(resp) {
    // Kept for backward compatibility
  }

  // ===== Authentication Methods =====

  loginWithEmailAndPassword(email: string, password: string) {
    this.store.dispatch(
      LoginActions.emailPassword({ email, password, fromRegister: false })
    );
  }

  async isWebhookDone() {
    return await this.appwriteService.isWebhookDone();
  }

  loginWithPhone(phone: string) {
    return this.appwriteService.loginWithPhone({ phone });
  }

  loginWithMagicLink(email: string) {
    return this.appwriteService.loginWithMagicLink({ email });
  }

  async handleCreatedSession(
    appwriteProfile,
    redirect: boolean = true,
    errorHandler: any = undefined
  ) {
    this.store.dispatch(TokenActions.generate());
  }

  handleMagicLinkCallback(userId: string, secret: string) {
    return this.appwriteService
      .handleMagicLinkCallback(userId, secret)
      .subscribe({
        next: () => this.store.dispatch(TokenActions.generate()),
        error: (error) => {
          this.toastr.error(error.message, 'Error!');
          this.router.navigate(['/']);
          this.store.dispatch(LoginActions.failure({ error }));
        },
      });
  }

  handlePhoneCallback(userId: string, secret: string) {
    return this.appwriteService
      .handlePhoneCallback(userId, secret)
      .subscribe({
        next: () => this.store.dispatch(TokenActions.generate()),
        error: (error) => {
          this.toastr.error(error.message, 'Error!');
          this.store.dispatch(LoginActions.failure({ error }));
        },
      });
  }

  logout(manual: boolean = true) {
    this.portalControlService.canActivateLogin = true;
    this.forget();
    if (!manual) {
      this.toastr.error('Session timeout!');
    }
    this.appwriteService.clearAppwriteSession();
    this.onLogout.emit(true);
    this.removeIdentity();
  }

  // ===== Registration Methods =====

  register(model: Partial<any>, errorHandler?: any) {
    this.store.dispatch(
      RegistrationActions.register({
        firstname: model.firstname,
        lastname: model.lastname,
        email: model.email,
        password: model.password,
        trackingCode: model.trackingCode,
      })
    );
  }

  validateBetaInvitation(token: string, errorHandler?: any) {
    return this.api.validateBetaInvitation(
      token,
      !!errorHandler ? errorHandler : this.handleError
    );
  }

  captureLead(model: Partial<any>, errorHandler?: any) {
    return this.api.captureLead(
      model,
      !!errorHandler ? errorHandler : this.handleError
    );
  }

  // ===== Profile Methods =====

  async getMyProfile(errorHandler?: any) {
    // Kept for backward compatibility - use store instead
    return this.currentUserValue;
  }

  async reloadMyProfile() {
    this.store.dispatch(UserProfileActions.loadLearmoProfile({ redirect: false }));
    this.store.dispatch(UserProfileActions.loadAppwriteProfile());
  }

  updateProfile(resp: any) {
    this.store.dispatch(UserProfileActions.setLearmoProfile({ learmoProfile: resp }));
  }

  async getSubscription(errorHandler?: any) {
    let subscription$ = this.api.getSubscriptionInfo(
      !!errorHandler ? errorHandler : this.handleError
    );

    await subscription$.toPromise().then((value) => {
      this.onGetSubscriptionResponse(value);
    });
  }

  public onGetSubscriptionResponse(resp) {
    const userObject: User = this.currentUserValue;
    if (!!userObject) {
      userObject.subscription = resp;
      localStorage.setItem(
        this.currentUserStorageKey,
        JSON.stringify(userObject)
      );
    } else {
      this.logout(false);
    }
  }

  // ===== Onboarding =====

  markWebsiteOnboardingAsDone(errorHandler?: any) {
    this.store.dispatch(OnboardingActions.markWebsiteSetupDone());
  }

  // ===== Auth Guard Methods =====

  async checkAuth(url: string = undefined) {
    return this.checkToken()
      .then((isTokenValid) => {
        if (!!isTokenValid) {
          return true;
        } else {
          return this.checkSession().then(async (isSessionValid) => {
            if (isSessionValid) {
              await this.refreshToken();
              return true;
            } else {
              throw new Error('No session found');
            }
          });
        }
      })
      .catch((error) => {
        throw new Error('No session found');
      });
  }

  private async checkToken(): Promise<boolean> {
    if (!!this.currentUserValue?.id && !!this.currentTokenValue) {
      return this.appwriteService
        .isTokenValid(this.currentTokenValue)
        .then((isTokenValid) => {
          if (isTokenValid) {
            return true;
          } else {
            return false;
          }
        })
        .catch((error) => {
          console.error('Error checking token validity:', error);
          return false;
        });
    }
  }

  private async checkSession(): Promise<boolean> {
    return this.appwriteService
      .isSessionValid()
      .then((isSessionValid) => {
        if (isSessionValid) {
          return true;
        } else {
          return false;
        }
      })
      .catch((error) => {
        console.error('Error checking session validity:', error);
        return false;
      });
  }

  refreshTokenObservable(): Observable<string> {
    return this.appwriteService.refreshToken().pipe(
      tap((token: string) => {
        this.store.dispatch(TokenActions.refreshSuccess({ token }));
      })
    );
  }

  async refreshToken(): Promise<string> {
    if (this.activeRefreshPromise) {
      return this.activeRefreshPromise;
    }

    this.store.dispatch(TokenActions.refresh());

    this.activeRefreshPromise = firstValueFrom(this.refreshTokenObservable())
      .then((token: string) => {
        this.currentTokenValue = token;
        localStorage.setItem(this.tokenStorageKey, token);
        this.appwriteService.setCurrentToken(token);
        return token;
      })
      .catch((err) => {
        console.error('Failed to refresh token:', err);
        throw err;
      })
      .finally(() => {
        this.activeRefreshPromise = null;
      });

    return this.activeRefreshPromise;
  }

  private handleError(error: any) {
    return error.message || 'Something went wrong during authorization.';
  }
}